import { useMemo } from "react";
import { Box, FormControlLabel, Switch } from "@mui/material";
import Addicon from "@mui/icons-material/Add";
import { ExpandMore } from "@mui/icons-material";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import { BtnPoper, Button, InfoContainer, Text } from "$components";
import JTable from "$components/JTable/JTable";
import JTooltip from "$components/JTooltip/JTooltip";
import DockyardIcon from "$containers/Dockyard/DockyardIcon";
import { ProjectStatus } from "$types/ProjectStatus";
import ProjectStatusBadge from "../../ProjectStatusBadge";
import { useStyles } from "../styles";
import { ProjectDetailsLogic } from "../types";

type ProjectDockyardsProps = Pick<
  ProjectDetailsLogic,
  | "active"
  | "canEdit"
  | "isProjectDisabled"
  | "project"
  | "handleSwitch"
  | "openPopinDockyard"
  | "setShowImportModal"
  | "setShowImportEditModal"
  | "dockyardTableData"
  | "handleSwitchDock"
>;

export default function ProjectDockyards({
  active,
  canEdit,
  isProjectDisabled,
  project,
  handleSwitch,
  openPopinDockyard,
  setShowImportModal,
  setShowImportEditModal,
  dockyardTableData,
  handleSwitchDock,
}: ProjectDockyardsProps) {
  const clasx = useStyles();

  const dockyardColumns = useMemo(
    () => [
      {
        id: "dockyardCode",
        Header: "Code chantier",
        Cell: ({ row }: any) => {
          const isDisabled =
            row.original.isDisable ||
            row.original.projectDisable ||
            row.original.companyDisable;
          return (
            <Text className={isDisabled ? clasx.DisabledText : ""}>
              {row.original.code}
            </Text>
          );
        },
      },
      {
        id: "chantier",
        Header: "Nom chantier",
        Cell: ({ row }: any) => {
          const isDisabled =
            row.original.isDisable ||
            row.original.projectDisable ||
            row.original.companyDisable;
          return (
            <Text
              className={isDisabled ? clasx.DisabledText : ""}
              type="important"
            >
              {row.original.name}
            </Text>
          );
        },
      },
      {
        id: "statut",
        Header: "Statut",
        Cell: ({ row }: any) => {
          const isDisabled =
            row.original.isDisable ||
            row.original.projectDisable ||
            row.original.companyDisable;
          return (
            <div role="button">
              <ProjectStatusBadge
                disabled={isDisabled}
                status={row.original.status}
              />
            </div>
          );
        },
      },
    ],
    [clasx.DisabledText]
  );

  return (
    <InfoContainer
      leftHeader={
        <div className={clasx.infoHeader}>
          <DockyardIcon />
          <Text type="h3">Chantier(s)</Text>
        </div>
      }
      rightHeader={
        <div style={{ position: "relative", display: "flex" }}>
          <FormControlLabel
            className={clasx.SwitchControl}
            label="Chantiers actifs"
            control={
              <Switch
                checked={active}
                onChange={handleSwitch}
                name="showCompany"
                color="primary"
              />
            }
          />
          <JTooltip
            title={
              isProjectDisabled
                ? "Vous ne pouvez pas ajouter de chantier à un projet inactif"
                : ""
            }
          >
            <span>
              <Button
                className={[
                  isProjectDisabled || project?.status === ProjectStatus.DONE
                    ? clasx.ctaDone
                    : "",
                ].join(" ")}
                variant="outlined"
                onClick={() => openPopinDockyard()}
                disabled={!canEdit || isProjectDisabled}
              >
                <Addicon />
                Ajouter un chantier
              </Button>
            </span>
          </JTooltip>
          <BtnPoper
            BtnLabel="Opérations en masse"
            BtnComponent={Button}
            BtnProps={{
              variant: "outlined",
              disabled: isProjectDisabled || !canEdit,
            }}
            BtnIcon={ExpandMore}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: "300px",
              }}
            >
              <Button
                onClick={() => setShowImportModal(true)}
                variant="text"
                disabled={isProjectDisabled}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 0,
                  color: "black !important",
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  padding: "6px",
                  minWidth: "300px",
                  "&:hover": {
                    backgroundColor: "#e0e0e0 !important",
                    color: "black !important",
                    border: "none !important",
                  },
                  "&.MuiButton-text:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "&.MuiButton-root:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "& svg": {
                    color: "black !important",
                    marginRight: "8px",
                  },
                  "& .MuiButton-label": {
                    color: "black !important",
                  },
                  "& span": {
                    color: "black !important",
                  },
                }}
              >
                <Download2LineIcon />
                Importer des chantiers
              </Button>
              <Button
                onClick={() => setShowImportEditModal(true)}
                variant="text"
                disabled={isProjectDisabled}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 0,
                  color: "black !important",
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  padding: "6px",
                  minWidth: "300px",
                  "&:hover": {
                    backgroundColor: "#e0e0e0 !important",
                    color: "black !important",
                    border: "none !important",
                  },
                  "&.MuiButton-text:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "&.MuiButton-root:hover": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                  "& svg": {
                    color: "black !important",
                    marginRight: "8px",
                  },
                  "& .MuiButton-label": {
                    color: "black !important",
                  },
                  "& span": {
                    color: "black !important",
                  },
                }}
              >
                <Edit2LineIcon />
                Mettre à jour des chantiers
              </Button>
            </Box>
          </BtnPoper>
        </div>
      }
    >
      <JTable
        data={dockyardTableData}
        columns={dockyardColumns}
        setPageIndex={() => {}}
        hideCheckboxes
        hideFilters
        rowsPerPage={50}
        noDataText={
          !dockyardTableData.length
            ? "Aucun chantier n'a encore été ajouté à ce projet"
            : undefined
        }
        usePaper={false}
        rowProps={(row) => ({
          onClick: () => handleSwitchDock(row.original),
          style: { cursor: "pointer" },
        })}
      />
    </InfoContainer>
  );
}
